import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, take } from 'rxjs/operators';
import { ApiChannel } from 'core/constants';
import { IXMOptions } from 'core/interfaces';

import { ModelBase } from 'store/model-base';
import { buildHeaders, Util, XmStore } from 'core/services';

import { ActivationInfo } from 'store/activation/models';
import { StoreAction } from 'store/actions';
import { Activations, Datasaver, DeviceNetworkCompatibility, LineCollection, LineDetail, LineDeviceCollection, LinePromotions, LineStatusCollection, LineStatuses, PlanEligibility, RequestResult } from './models';
import { RequestStatus } from 'store/request-status/models';
import { OrderDetail } from 'store/order/models';
import { ChangeNotificationResult } from './models/change-notification';
import { AddonCollections } from 'store/addons/addon-collections';
import { ProgressStatus } from 'my-account/constants';
import { LineTransferCollection } from './models/line-transfer/line-transfer-collection';
import { LineTransferBySessionId } from './models/line-transfer/line-transfer-by-session-id';
import { PendingLineTransferCollection } from './models/line-transfer/pending-line-transfer-collection';
import { SendPortOutPin } from './models/send-port-out-pin';
import { SendOTP } from './models/send-otp';
import { InsuranceElibility } from './models/insurance-eligibility';

@Injectable()
export class LineApi {
    public static getLines(_xmStore: XmStore, http: HttpClient): Observable<LineCollection> {
        return http.get('/account/lines', {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((linesResponse: LineDetail[]) => LineCollection.create<LineCollection>({ lines: linesResponse }))
        );
    }

    public static getLinesViaSSMProxy(_xmStore: XmStore, http: HttpClient): Observable<LineCollection> {
        return http.get('/xm/mobile/lines', {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((linesResponse: LineDetail[]) => LineCollection.create<LineCollection>({ lines: linesResponse }))
        );
    }

    public static getLinePromotions(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<LinePromotions> {
        return http.get(`/lines/${params.lineKey}/promotions`, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: LinePromotions) => LinePromotions.create<LinePromotions>({ lineKey: params.lineKey, promos: response }))
        );
    }

    public static getLinesAndOneActivation(xmStore: XmStore, http: HttpClient, params: ApiParams, config: IXMOptions): Observable<LineCollection> {
        const lineAction: string = config.ENABLE_INSTANT_ACTIVATION ? StoreAction.GET_LINES_VIA_SSM_PROXY : StoreAction.GET_LINE_COLLECTION;

        return xmStore.find<LineCollection>(lineAction, LineCollection).pipe(
            mergeMap((response: LineCollection) => {
                const foundLine: LineDetail = response.lines.find((line: LineDetail) => line.id.key === params.line_id);

                if (foundLine.pendingDevice && foundLine.id.key) {
                    return http.get(`/lines/${foundLine.id.key}/activation`, {
                        headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
                    }).pipe(
                        map((activationInfo: ActivationInfo) => {
                            activationInfo.errorCode = activationInfo.errorCode && activationInfo.errorCode.toUpperCase();
                            foundLine.activationInfo = activationInfo;

                            return response;
                        }),
                        catchError(() => of(response))
                    );
                }

                return of(response);
            }),
            map((response: LineCollection) => LineCollection.create<LineCollection>(response))
        );
    }

    public static getLinesAndOneOrderDetail(xmStore: XmStore, http: HttpClient, params: ApiParams, config: IXMOptions): Observable<LineCollection> {
        const lineAction: string = config.ENABLE_INSTANT_ACTIVATION ? StoreAction.GET_LINES_VIA_SSM_PROXY : StoreAction.GET_LINE_COLLECTION;

        return xmStore.find<LineCollection>(lineAction, LineCollection).pipe(
            mergeMap((response: LineCollection) => {
                const foundLine: LineDetail = response.lines.find((line: LineDetail) => line.id.key === params.line_id);

                if (foundLine.pendingDevice && foundLine.pendingDevice.orderId) {
                    return http.get(`/orders/${foundLine.pendingDevice.orderId.key}`, {
                        headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
                    }).pipe(
                        map((orderDetail: OrderDetail) => {
                            foundLine.pendingDevice.orderDetail = orderDetail;

                            return response;
                        }),
                        catchError(() => of(response))
                    );
                }

                return of(response);
            }),
            map((response: LineCollection) => LineCollection.create<LineCollection>(response))
        );
    }

    public static getActivationInfoForLine(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<ActivationInfo> {
        const lineKey: string = params.lineId;

        return http.get(`/lines/${lineKey}/activation`, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ActivationInfo) => {
                response.errorCode = response.errorCode && response.errorCode.toUpperCase();

                return ActivationInfo.create<ActivationInfo>(response);
            })
        );
    }

    public static updateActivationInfo(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<LineCollection> {
        const lineCollection: LineCollection = params.lineCollection;
        const line: LineDetail = params.line;
        const checkPortability: boolean = params.checkPortability || false;
        const activationInfo: object = line.activationInfo.convertToJSON();

        return http.post(`/lines/${line.id.key}/activation`, activationInfo, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY }),
            params: { checkPortability: `${checkPortability}` }
        }).pipe(
            map(() => lineCollection)
        );
    }

    public static activateLine(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<LineCollection> {
        const lineCollection: LineCollection = params.lineCollection;
        const line: LineDetail = params.line;
        const queryParam: string = !(line.pendingDevice.isPending || line.pendingDevice.isByodExchange) ? Util.createQueryParams({ type: line.pendingDevice.status }) : '';

        return http.post(`/lines/${line.id.key}/activate${queryParam}`, {}, {
            headers: buildHeaders({
                apiChannel: ApiChannel.GATEWAY,
                'X-Channel': 'WEB'
            })
        }).pipe(
            map(() => lineCollection)
        );
    }

    public static suspendLine(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<RequestStatus> {
        return http.post(`/lines/${params.lineId}/suspend`, undefined, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => RequestStatus.create<RequestStatus>(response))
        );
    }

    public static sendPin(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<ApiResponse> {
        return http.post(`/account/${params.accountId}/pin/notification`, params.payload, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY }),
            params: { runEligibility: 'true' }
        });
    }

    public static changeNotification(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<ChangeNotificationResult> {
        return http.post('/account/change/notification', params.payload, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ChangeNotificationResult) => ChangeNotificationResult.create<ChangeNotificationResult>(response))
        );
    }

    public static sendPortOutPin(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<SendPortOutPin> {
        return http.post('/account/portout/pin/notification', params.payLoad, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => SendPortOutPin.create<SendPortOutPin>(response))
        );
    }

    public static updateDisplayName(xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<LineCollection> {
        return http.post(`/lines/${params.lineId}/displayname`, params.payload, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            mergeMap(() => xmStore.peek<LineCollection>(LineCollection).pipe(
                map((collection: LineCollection) => {
                    const line: LineDetail = collection.getLineByKey(params.lineId);
                    line.displayName = params.payload.displayName;

                    return collection;
                })
            )),
            take(1)
        );
    }

    public static persistLines(_xmStore: XmStore, _http: HttpClient, params: ApiParams): Observable<LineCollection> {
        return of(params.lineCollection);
    }

    public static dppPayment(xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<LineCollection> {
        return http.post(`/dpps/${params.deviceId}/payment`, { amount: params.amount }, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            mergeMap(() => xmStore.peek<LineCollection>(LineCollection).pipe(
                map((collection: LineCollection) => collection))
            ),
            take(1)
        );
    }

    public static getPlanEligibility(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<PlanEligibility> {
        const lineKey: string = params.lineKey;

        return http.get(`/lines/${lineKey}/plans/switcheligibility`, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY }),
            params: { switchType: params.switchType }
        }).pipe(
            map((response: PlanEligibility) => PlanEligibility.create<PlanEligibility>({ ...response, lineKey }))
        );
    }

    public static cancelPlanScheduledDowngrade(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<RequestStatus> {
        return http.post(`/lines/${params.lineKey}/plan/scheduled/cancel`, { reason: 'CREQ' }, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => RequestStatus.create<RequestStatus>(response))
        );
    }

    public static cancelPlanScheduledDowngradeViaSSMProxy(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<RequestStatus> {
        return http.post('/xm/ot/post/scheduled/cancel', { lineIdKey: params.lineKey, reason: 'cancel' }, {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: ApiResponse) => RequestStatus.create<RequestStatus>(response))
        );
    }

    public static getDataSaverStatus(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<Datasaver> {
        const lineKey: string = params.lineKey;

        return http.get(`/lines/${lineKey}/features/datasaver/status`, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: Datasaver) => Datasaver.create<Datasaver>({ ...response, lineKey }))
        );
    }

    public static updateDatasaverStatus(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<RequestStatus> {
        return http.post(`/lines/${params.lineKey}/features/datasaver`, { state: params.state }, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => RequestStatus.create<RequestStatus>(response))
        );
    }

    public static getAddons(_xmStore: XmStore, http: HttpClient, param: ApiParams): Observable<AddonCollections> {
        const lineId: string = param.lineId;

        return http.get(`/lines/${lineId}/plans/addons`, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((addons: ApiResponse) => AddonCollections.create<AddonCollections>({ addons, lineId }))
        );
    }

    public static addAddon(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<RequestStatus> {
        return http.post(`/lines/${params.lineId}/plans/${params.planId}/addons/add`, {}, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => RequestStatus.create<RequestStatus>(response))
        );
    }

    public static removeAddon(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<RequestStatus> {
        return http.post(`/lines/${params.lineId}/plans/${params.planId}/addons/remove`, {}, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => RequestStatus.create<RequestStatus>(response))
        );
    }

    public static cancelRemoveAddon(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<RequestStatus> {
        return http.post(`/lines/${params.lineId}/plans/addons/orders/${params.removalRequestCode}/cancel`, {}, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY }),
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<ApiResponse>) => {
                let status: string = ProgressStatus.FAIL;

                if (response.status === 202) {
                    status = ProgressStatus.SUCCESS;
                }

                return RequestStatus.create<RequestStatus>({ status });
            })
        );
    }

    public static getLineDeviceCollection(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<LineDeviceCollection> {
        return http.get(`/lines/${params.lineId}/devices`, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) =>
                LineDeviceCollection.create<LineDeviceCollection>({ ...response, lineId: params.lineId }))
        );
    }

    public static getDeviceNetworkCompatibility(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<DeviceNetworkCompatibility> {
        return http.get(`/lines/${params.key}/device/${params.imei}/network/compatibility`, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: DeviceNetworkCompatibility) => DeviceNetworkCompatibility.create<DeviceNetworkCompatibility>(response))
        );
    }

    public static postAlignNetworkFeatures(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<RequestStatus> {
        return http.post(`/lines/${params.lineId}/alignnetworkfeature`, params.payLoad, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: HttpResponse<ApiResponse>) => {
                let status: string = '';

                if (response.status === 200) {
                    status = ProgressStatus.SUCCESS;
                }

                return RequestStatus.create<RequestStatus>({ ...response, status });
            })
        );
    }

    public static saveRequestResult(_xmStore: XmStore, _http: HttpClient, status: string): Observable<RequestResult> {
        return of(RequestResult.create<RequestResult>(status));
    }

    public static getLineTransfer(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<LineTransferCollection> {
        const accountIdKey: string = params.accountIdKey;

        return http.get(`/accounts/${accountIdKey}/lines/transfers`, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((lineTransfers: ApiResponse) => LineTransferCollection.create<LineTransferCollection>({ lineTransfers, accountIdKey }))
        );
    }

    public static getLineTransferBySessionId(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<LineTransferBySessionId> {
        const sessionId: string = params.transferSessionId;

        return http.get(`/accounts/lines/transfer/${sessionId}`, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => LineTransferBySessionId.create<LineTransferBySessionId>(response))
        );
    }

    public static requestNewPin(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<ApiResponse> {
        return http.post(`/accounts/lines/transfer/${params.transferOrderId}/pin/resend`, {}, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        });
    }

    public static getPendingLineTransfer(_xmStore: XmStore, http: HttpClient): Observable<PendingLineTransferCollection> {

        return http.get('/account/lines/transfers/pending', {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((pendingLineTransfers: ApiResponse) => PendingLineTransferCollection.create<PendingLineTransferCollection>({ pendingLineTransfers }))
        );
    }

    public static cancelLineTransfer(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<ApiResponse> {
        return http.post(`/orders/${params.orderId}/cancellation`, {}, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        });
    }

    public static sendOtp(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<SendOTP> {
        return http.post('/lines/activations/sendOTP', params.payload, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => SendOTP.create<SendOTP>(response))
        );
    }

    public static esimActivationVerifyOtpCb(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<ApiResponse> {
        return http.post('/lines/activations/verifyOTP', params.payload, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        });
    }

    public static linesDevicesByodOrder(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<ApiResponse> {
        return http.post(`/lines/${params.lineIdKey}/devices/byod`, params.payload, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        });
    }

    public static getInsuranceElibility(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<InsuranceElibility> {
        return http.post('/account/insuranceeligibility', params.payload, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => InsuranceElibility.create<InsuranceElibility>(response))
        );
    }

    public static getActivations(_xmStore: XmStore, http: HttpClient): Observable<Activations> {
        return http.get('/activations', {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => Activations.create<Activations>(response))
        );
    }

    public static getLineStatus(_xmStore: XmStore, http: HttpClient): Observable<LineStatusCollection> {
        return http.get('/xm/mobile/lines/status', {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: LineStatuses[]) => LineStatusCollection.create<LineStatusCollection>({ lineStatuses: response }))
        );
    }
}

ModelBase.fetchMapping[StoreAction.GET_LINE_COLLECTION] = LineApi.getLines;
ModelBase.fetchMapping[StoreAction.GET_ACTIVATION_INFO_FOR_LINE] = LineApi.getActivationInfoForLine;
ModelBase.fetchMapping[StoreAction.GET_LINES_AND_ONE_ACTIVATION] = LineApi.getLinesAndOneActivation;
ModelBase.fetchMapping[StoreAction.GET_LINES_AND_ONE_ORDER_DETAIL] = LineApi.getLinesAndOneOrderDetail;
ModelBase.fetchMapping[StoreAction.GET_LINE_LEVEL_PLAN_ELIGIBILITY] = LineApi.getPlanEligibility;
ModelBase.fetchMapping[StoreAction.GET_DATASAVER_STATUS] = LineApi.getDataSaverStatus;
ModelBase.fetchMapping[StoreAction.GET_DEVICE_NETWORK_COMPATIBILITY] = LineApi.getDeviceNetworkCompatibility;
ModelBase.fetchMapping[StoreAction.GET_LINE_DEVICES] = LineApi.getLineDeviceCollection;
ModelBase.fetchMapping[StoreAction.GET_ADDONS_COLLECTION] = LineApi.getAddons;
ModelBase.fetchMapping[StoreAction.GET_LINE_PROMOTIONS] = LineApi.getLinePromotions;
ModelBase.fetchMapping[StoreAction.GET_LINE_TRANSFER] = LineApi.getLineTransfer;
ModelBase.fetchMapping[StoreAction.GET_LINE_TRANSFER_BY_SESSION_ID] = LineApi.getLineTransferBySessionId;
ModelBase.fetchMapping[StoreAction.GET_PENDING_LINE_TRANSFER] = LineApi.getPendingLineTransfer;
ModelBase.fetchMapping[StoreAction.GET_ACTIVATIONS] = LineApi.getActivations;
ModelBase.fetchMapping[StoreAction.GET_LINE_STATUS] = LineApi.getLineStatus;
ModelBase.persistMapping[StoreAction.POST_ALIGN_NETWORK_FEATURES] = LineApi.postAlignNetworkFeatures;
ModelBase.persistMapping[StoreAction.UPDATE_DATASAVER_STATUS] = LineApi.updateDatasaverStatus;
ModelBase.persistMapping[StoreAction.UPDATE_ACTIVATION_INFO] = LineApi.updateActivationInfo;
ModelBase.persistMapping[StoreAction.POST_ACTIVATE_LINE] = LineApi.activateLine;
ModelBase.persistMapping[StoreAction.SUSPEND_LINE] = LineApi.suspendLine;
ModelBase.persistMapping[StoreAction.PERSIST_LINES] = LineApi.persistLines;
ModelBase.persistMapping[StoreAction.POST_DPP_PAYMENT] = LineApi.dppPayment;
ModelBase.persistMapping[StoreAction.UPDATE_DISPLAY_NAME] = LineApi.updateDisplayName;
ModelBase.persistMapping[StoreAction.SEND_PIN] = LineApi.sendPin;
ModelBase.persistMapping[StoreAction.ADD_ADDON] = LineApi.addAddon;
ModelBase.persistMapping[StoreAction.REMOVE_ADDON] = LineApi.removeAddon;
ModelBase.persistMapping[StoreAction.CANCEL_REMOVE_ADDON] = LineApi.cancelRemoveAddon;
ModelBase.persistMapping[StoreAction.SAVE_REQUEST_RESULT] = LineApi.saveRequestResult;
ModelBase.persistMapping[StoreAction.REQUEST_NEW_PIN] = LineApi.requestNewPin;
ModelBase.persistMapping[StoreAction.CANCEL_LINE_TRANSFER] = LineApi.cancelLineTransfer;
ModelBase.persistMapping[StoreAction.SEND_OTP] = LineApi.sendOtp;
ModelBase.persistMapping[StoreAction.VERIFY_ACTIVATION_OTP_CB] = LineApi.esimActivationVerifyOtpCb;
ModelBase.persistMapping[StoreAction.POST_LINES_DEVICES_BYOD_ORDER] = LineApi.linesDevicesByodOrder;
ModelBase.persistMapping[StoreAction.POST_CANCEL_PLAN_SCHEDULED_DOWNGRADE] = LineApi.cancelPlanScheduledDowngrade;
ModelBase.persistMapping[StoreAction.POST_CANCEL_PLAN_SCHEDULED_DOWNGRADE_VIA_SSM_PROXY] = LineApi.cancelPlanScheduledDowngradeViaSSMProxy;
ModelBase.persistMapping[StoreAction.POST_CHANGE_NOTIFICATION] = LineApi.changeNotification;
ModelBase.persistMapping[StoreAction.SEND_PORT_OUT_PIN] = LineApi.sendPortOutPin;
ModelBase.persistMapping[StoreAction.GET_INSURANCE_ELIGIBILITY] = LineApi.getInsuranceElibility;
ModelBase.fetchMapping[StoreAction.GET_LINES_VIA_SSM_PROXY] = LineApi.getLinesViaSSMProxy;
